import { useState, type ReactNode } from "react";
import { descriviVerdetto } from "../../i18n/rilascio.ts";
import { api } from "../lib/api.ts";
import { useT } from "../lib/i18n.tsx";
import { useAction, useQuery } from "../lib/hooks.ts";
import { percorsoBlueGreen } from "../lib/percorso.ts";
import { SEZIONI_RILASCIO, type SezioneRilascio } from "../lib/risorse.ts";
import { navigate } from "../lib/router.ts";
import { serviziInstradabili, type ServizioInstradabile } from "../lib/rilascio.ts";
import type { ChiaveRequisito, Requisito, VerdettoRilascio } from "../lib/types.ts";
import { Apribile, Badge, Button, Card, ErrorNotice, Field, Input, Spinner, cx } from "./ui.tsx";

/**
 * Configurazione del rilascio: trigger automatico e instradamento.
 *
 * Le due cose stanno insieme perché rispondono alla stessa domanda — come va in
 * produzione questo stack — e perché il rilascio senza interruzione richiede
 * entrambe: un dominio a cui spostare il traffico e, di solito, qualcuno che
 * lo attivi.
 */

interface Webhook {
  readonly tokenHint: string;
  readonly tagVariable: string;
  readonly lastUsedAt: string | null;
}

interface Chiamata {
  readonly id: number;
  readonly outcome: "accepted" | "rejected";
  readonly reason: string | null;
  readonly tag: string | null;
  readonly createdAt: string;
}

interface Instradamento {
  readonly id: number;
  readonly service: string;
  readonly domain: string;
  readonly containerPort: number;
}

/** Ciò che `GET /stacks/:id/instradamenti` restituisce: lo legge la pagina, una volta sola. */
export interface DatiInstradamenti {
  readonly routes: Instradamento[];
  readonly verdetto: VerdettoRilascio;
  readonly servizi: { name: string; portaProposta: number | null; piuPorte: boolean; fisso: boolean }[];
  readonly proxyInstallabile: boolean;
}

/**
 * La scheda Rilascio, dall'alto: il percorso verso il blue/green, i controlli
 * prima dell'avvio, gli instradamenti, l'automazione dalla CI.
 *
 * Percorso e instradamenti leggono lo stesso verdetto, quello che la pagina ha
 * già chiesto per la striscia in alto: due richieste potrebbero contraddirsi.
 */
export function SchedaRilascio({
  stackId,
  dati,
  errore,
  modificabile,
  controlli,
  sezione,
  onSezione,
  onInstradamentiAggiornati,
  onConfigurazione,
}: {
  stackId: number;
  dati: DatiInstradamenti | null;
  errore: unknown;
  modificabile: boolean;
  /** I controlli prima dell'avvio: lavorano sulla bozza, che vive nella pagina. */
  controlli: ReactNode;
  sezione: SezioneRilascio;
  onSezione: (s: SezioneRilascio) => void;
  /** Ricarica il verdetto e chi altro, fuori da questa scheda, ne mostra un effetto. */
  onInstradamentiAggiornati: () => void;
  onConfigurazione: () => void;
}) {
  const t = useT();
  // Senza il verdetto strategia e instradamenti non hanno niente da mostrare.
  const attesa = !errore && <Spinner className="tenue" />;

  return (
    <div className="space-y-6">
      {/* Pillole, non linguette: si distinguono dalle schede della pagina sopra.
          Vanno a capo invece di scorrere: una sotto-scheda fuori schermo è
          proprio ciò che queste pillole devono evitare. */}
      <nav className="flex flex-wrap gap-1">
        {SEZIONI_RILASCIO.map((nome) => (
          <button
            key={nome}
            onClick={() => onSezione(nome)}
            aria-current={sezione === nome ? "page" : undefined}
            className={cx(
              "rounded-full px-3 py-1 text-sm",
              sezione === nome
                ? "bg-[var(--superficie-tenue)] font-medium"
                : "tenue hover:text-[var(--testo)]",
            )}
          >
            {t.t(`rilascioUi.sezione.${nome}` as const)}
          </button>
        ))}
      </nav>
      <ErrorNotice error={errore} />
      {sezione === "controlli" && controlli}
      {sezione === "strategia" &&
        (dati ? (
          <PercorsoBlueGreen
            stackId={stackId}
            verdetto={dati.verdetto}
            modificabile={modificabile}
            proxyInstallabile={dati.proxyInstallabile}
            onCambiata={onInstradamentiAggiornati}
            onConfigurazione={onConfigurazione}
            onInstrada={() => onSezione("instradamenti")}
          />
        ) : (
          attesa
        ))}
      {sezione === "instradamenti" &&
        (dati ? (
          <PannelloInstradamenti
            stackId={stackId}
            dati={dati}
            modificabile={modificabile}
            onInstradamentiAggiornati={onInstradamentiAggiornati}
          />
        ) : (
          attesa
        ))}
      {sezione === "automazione" && <PannelloWebhook stackId={stackId} modificabile={modificabile} />}
    </div>
  );
}

function PannelloWebhook({ stackId, modificabile }: { stackId: number; modificabile: boolean }) {
  const { data, error, reload } = useQuery<{ webhook: Webhook | null; calls: Chiamata[] }>(
    `/stacks/${stackId}/webhook`,
  );

  const t = useT();
  /** Il token in chiaro esiste solo subito dopo la generazione. */
  const [appenaCreato, setAppenaCreato] = useState<{ url: string; token: string } | null>(null);

  const generazione = useAction(async () => {
    const risposta = await api.post<{ url: string; token: string }>(
      `/stacks/${stackId}/webhook`,
      {},
    );
    setAppenaCreato({ url: risposta.url, token: risposta.token });
    reload();
  });

  const revoca = useAction(async () => {
    await api.delete(`/stacks/${stackId}/webhook`);
    setAppenaCreato(null);
    reload();
  });

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold">{t.t("ci.titolo")}</h2>
        <p className="mt-0.5 text-sm tenue">{t.t("ci.intro")}</p>
      </div>

      <ErrorNotice error={error ?? generazione.error ?? revoca.error} />

      {appenaCreato && (
        <Card className="border-accent-500/40 p-4">
          <p className="text-xs font-medium">{t.t("ci.copiaOra")}</p>
          {/* Il token viaggia nell'header e non nel percorso: un indirizzo
              finisce nei log di ogni proxy che attraversa, un header no. */}
          <code className="mt-2 block break-all rounded-lg bg-[var(--superficie-tenue)] p-2.5 font-mono text-xs">
            {`curl -X POST ${appenaCreato.url} -H "Authorization: Bearer ${appenaCreato.token}" -d '{"tag":"v1.2.3"}'`}
          </code>
          <p className="mt-2 text-xs tenue">{t.t("ci.conservaSegreto")}</p>
        </Card>
      )}

      {data?.webhook ? (
        <Card className="space-y-3 p-4">
          <div className="flex items-center gap-3 text-sm">
            <Badge tone="success">{t.t("ci.attivo")}</Badge>
            <code className="text-xs tenue">{t.t("ci.token", { indizio: data.webhook.tokenHint })}</code>
            <span className="ml-auto text-xs tenue">
              {t.t(data.webhook.lastUsedAt ? "ci.usatoDiRecente" : "ci.maiUsato")}
            </span>
          </div>

          <p className="text-xs tenue">{t.t("ci.variabileTag", { variabile: data.webhook.tagVariable })}</p>

          {modificabile && (
            <div className="flex gap-2">
              <Button className="text-xs" loading={generazione.running} onClick={() => void generazione.run()}>
                {t.t("ci.rigenera")}
              </Button>
              <Button
                variant="ghost"
                className="text-xs text-red-600"
                loading={revoca.running}
                onClick={() => void revoca.run()}
              >
                {t.t("ci.revoca")}
              </Button>
            </div>
          )}
        </Card>
      ) : (
        modificabile && (
          <Button variant="primary" loading={generazione.running} onClick={() => void generazione.run()}>
            {t.t("ci.genera")}
          </Button>
        )
      )}

      {data && data.calls.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold tenue">{t.t("ci.ultimeChiamate")}</h3>
          <Card className="divide-y overflow-hidden">
            {data.calls.slice(0, 8).map((chiamata) => (
              <div key={chiamata.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                <Badge tone={chiamata.outcome === "accepted" ? "success" : "danger"}>
                  {t.t(chiamata.outcome === "accepted" ? "ci.accettata" : "ci.rifiutata")}
                </Badge>
                <span className="truncate tenue">{chiamata.tag ?? chiamata.reason}</span>
              </div>
            ))}
          </Card>
        </div>
      )}
    </section>
  );
}

interface AnteprimaCorrezione {
  readonly rimosse: { readonly servizio: string; readonly voce: string }[];
  readonly nonGestiti: { readonly servizio: string; readonly motivo: string }[];
}

/**
 * Toglie le porte host dal compose, dopo aver mostrato cosa toglierà.
 *
 * È l'unico dei cinque requisiti che una macchina può soddisfare da sé: il
 * proxy sono variabili del processo, il dominio lo sa solo chi lo possiede. Ma
 * tocca il compose, che è dell'utente, quindi prima lo dichiara riga per riga —
 * e quando una forma non si toglie in sicurezza lo dice, invece di indovinare.
 *
 * Il salvataggio passa dalla rotta delle revisioni come qualunque modifica:
 * nasce una revisione, e dallo storico si torna indietro.
 */
function CorrezionePorte({ stackId, onCorretto }: { stackId: number; onCorretto: () => void }) {
  const t = useT();
  const [anteprima, setAnteprima] = useState<AnteprimaCorrezione | null>(null);

  const caricamento = useAction(async () => {
    setAnteprima(await api.get<AnteprimaCorrezione>(`/stacks/${stackId}/correzioni/porte`));
  });

  // L'applicazione la esegue il server: il compose corretto non torna indietro
  // da qui, e soprattutto non ci passa l'environment, che il salvataggio cifra
  // — rimandarlo dal browser riscriverebbe i segreti col loro mascheramento.
  const applicazione = useAction(async () => {
    await api.post(`/stacks/${stackId}/correzioni/porte`);
    setAnteprima(null);
    onCorretto();
  });

  if (!anteprima) {
    return (
      <Button
        className="shrink-0 text-xs"
        loading={caricamento.running}
        onClick={() => void caricamento.run()}
      >
        {t.t("porteHost.correggi")}
      </Button>
    );
  }

  return (
    <div className="mt-2 w-full space-y-3 border-t pt-3">
      <ErrorNotice error={caricamento.error ?? applicazione.error} />

      {anteprima.rimosse.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium">{t.t("porteHost.rimosse")}</p>
          <Card className="divide-y overflow-hidden">
            {anteprima.rimosse.map((rimossa) => (
              <div key={`${rimossa.servizio}-${rimossa.voce}`} className="flex gap-3 px-3 py-1.5 text-xs">
                <code className="font-mono font-medium">{rimossa.servizio}</code>
                <code className="min-w-0 truncate font-mono tenue">{rimossa.voce}</code>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Una forma che non si toglie riga per riga resta all'utente: dirlo è
          parte della correzione, perché altrimenti il requisito resterebbe
          rosso senza che si capisca quale servizio lo tiene tale. */}
      {anteprima.nonGestiti.length > 0 && (
        <p className="text-xs tenue">
          {t.t("porteHost.aMano", {
            servizi: anteprima.nonGestiti
              .map((s) => `${s.servizio} (${t.traduci({ chiave: s.motivo }) ?? s.motivo})`)
              .join(", "),
          })}
        </p>
      )}

      {anteprima.rimosse.length === 0 ? (
        <p className="text-xs tenue">{t.t("porteHost.nulla")}</p>
      ) : (
        <p className="text-xs tenue">{t.t("porteHost.revisione")}</p>
      )}

      <div className="flex gap-2">
        {anteprima.rimosse.length > 0 && (
          <Button
            variant="primary"
            className="text-xs"
            loading={applicazione.running}
            onClick={() => void applicazione.run()}
          >
            {t.t("porteHost.rimuovi")}
          </Button>
        )}
        <Button variant="ghost" className="text-xs" onClick={() => setAnteprima(null)}>
          {t.t("comune.annulla")}
        </Button>
      </div>
    </div>
  );
}

/**
 * Il rilascio senza interruzione come percorso a passi numerati.
 *
 * Prima era un elenco di nove condizioni tutte uguali: chi lo leggeva non
 * sapeva da quale cominciare, e le sei già soddisfatte coprivano le tre da
 * fare. Ora i passi sono le scelte da fare, in ordine, ciascuno col suo perché,
 * lo stato attuale e il gesto che lo risolve; il compose compare come passo
 * solo dove blocca. Gli avvertimenti restano accanto: non impediscono il
 * rilascio, ma ne cambiano la garanzia, e tacerli sarebbe lasciarli scoprire
 * a rilascio già avvenuto.
 */
function PercorsoBlueGreen({
  stackId,
  verdetto,
  modificabile,
  proxyInstallabile,
  onCambiata,
  onConfigurazione,
  onInstrada,
}: {
  stackId: number;
  verdetto: VerdettoRilascio;
  modificabile: boolean;
  proxyInstallabile: boolean;
  onCambiata: () => void;
  onConfigurazione: () => void;
  onInstrada: () => void;
}) {
  const t = useT();
  // Le frasi sullo stato sono quelle che riceve anche la CI quando un avvio è rifiutato.
  const descritto = descriviVerdetto(verdetto, t);
  const statoDi = (chiave: ChiaveRequisito) =>
    descritto.requisiti.find((r) => r.chiave === chiave)!;
  const { passi, compatibili, corrente } = percorsoBlueGreen(verdetto);
  const strategiaAttiva = passi[0]!.soddisfatto;

  const cambioStrategia = useAction(async () => {
    await api.patch(`/stacks/${stackId}`, {
      deployStrategy: strategiaAttiva ? "recreate" : "blue-green",
    });
    onCambiata();
  });

  /** Il gesto che risolve il passo, dove esiste. */
  const azione = (passo: Requisito): ReactNode => {
    switch (passo.chiave) {
      // La strategia si cambia da qui anche quando è già scelta: è l'unica
      // condizione che dipende da una scelta, e tornare indietro è legittimo.
      case "strategia":
        return (
          modificabile && (
            <Button
              variant={corrente === "strategia" ? "primary" : "secondary"}
              className="shrink-0 text-xs"
              loading={cambioStrategia.running}
              onClick={() => void cambioStrategia.run()}
            >
              {t.t(strategiaAttiva ? "rilascioUi.tornaRicreazione" : "rilascioUi.passaBlueGreen")}
            </Button>
          )
        );
      case "proxy":
        return !passo.soddisfatto && proxyInstallabile && <InstallaProxy />;
      case "instradamento":
        return (
          !passo.soddisfatto &&
          modificabile && (
            <Button
              className="shrink-0 text-xs"
              onClick={onInstrada}
            >
              {t.t("rilascioUi.instrada")}
            </Button>
          )
        );
      // Le porte host sono l'unico requisito che una macchina può soddisfare
      // da sé, perché la risposta sta tutta nel compose.
      case "porte":
        return modificabile && <CorrezionePorte stackId={stackId} onCorretto={onCambiata} />;
      default:
        return (
          <Button className="shrink-0 text-xs" onClick={onConfigurazione}>
            {t.t("rilascioUi.apriConfigurazione")}
          </Button>
        );
    }
  };

  const contenuto = (
    <div className="space-y-3">
      <div>
        <p className="text-sm tenue">{t.t("rilascioUi.intro")}</p>
        {!verdetto.pronto && <p className="mt-1 text-xs tenue">{descritto.effetto}</p>}
      </div>

      <ol className="divide-y border-t">
        {passi.map((passo, indice) => {
          const stato = statoDi(passo.chiave);
          const èCorrente = passo.chiave === corrente;
          return (
            <li key={passo.chiave} className="flex flex-wrap items-start gap-3 py-3">
              <span
                className={cx(
                  "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                  passo.soddisfatto
                    ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                    : èCorrente
                      ? "bg-accent-600 text-white"
                      : "border tenue",
                )}
              >
                {passo.soddisfatto ? "✓" : indice + 1}
              </span>
              <div className="min-w-0 flex-1 text-sm">
                <p className={cx("font-medium", èCorrente && "text-accent-700 dark:text-accent-500")}>
                  {t.t(`percorso.${passo.chiave}.titolo` as const)}
                </p>
                <p className="text-xs tenue">{t.t(`percorso.${passo.chiave}.perche` as const)}</p>
                <p className="mt-1 text-xs">{stato.comeSoddisfarlo}</p>
              </div>
              {azione(passo)}
            </li>
          );
        })}
      </ol>

      {compatibili.length > 0 && (
        <Apribile titolo={<span className="text-sm">{t.t("rilascioUi.compatibile", { n: compatibili.length })}</span>}>
          <ul className="space-y-2 text-xs">
            {compatibili.map((requisito) => (
              <li key={requisito.chiave}>
                <span className="text-emerald-600 dark:text-emerald-400" aria-hidden>
                  ✓{" "}
                </span>
                {statoDi(requisito.chiave).titolo}
                <span className="block tenue">{statoDi(requisito.chiave).comeSoddisfarlo}</span>
              </li>
            ))}
          </ul>
        </Apribile>
      )}

      {descritto.avvertimenti.map((avvertimento) => (
        <div key={avvertimento.chiave} className="flex items-start gap-2 text-xs">
          <span className="mt-px shrink-0 text-amber-500" aria-hidden>
            ⚠
          </span>
          <span className="min-w-0 flex-1">
            {avvertimento.titolo}
            <span className="block tenue">{avvertimento.dettaglio}</span>
          </span>
        </div>
      ))}

      <ErrorNotice error={cambioStrategia.error} />
    </div>
  );

  // Ha una sotto-scheda tutta sua: anche a percorso fatto resta aperto, per
  // rileggerlo o per tornare alla ricreazione.
  const k = passi.findIndex((p) => p.chiave === corrente) + 1;
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-3">
        <h2 className="text-sm font-semibold">{t.t(verdetto.pronto ? "rilascioUi.attivo" : "rilascioUi.titolo")}</h2>
        {!verdetto.pronto && (
          <span className="ml-auto text-xs tenue">{t.t("rilascioUi.passo", { k, n: passi.length })}</span>
        )}
      </div>
      <Card className="p-4">{contenuto}</Card>
    </section>
  );
}

/**
 * Installa Nginx Proxy Manager come stack, con le impostazioni consigliate.
 *
 * Le porte si scelgono qui perché il deploy parte subito: con la 80 già
 * occupata fallirebbe prima che ci sia modo di cambiarla.
 */
function InstallaProxy() {
  const t = useT();
  const [aperto, setAperto] = useState(false);
  const [http, setHttp] = useState("80");
  const [https, setHttps] = useState("443");

  // Si va allo stack del proxy: è lì che si segue il primo avvio, circa un minuto.
  const installazione = useAction(async () => {
    const { stack } = await api.post<{ stack: { id: number } }>("/impostazioni/proxy", {
      http: Number(http),
      https: Number(https),
    });
    navigate(`/stacks/${stack.id}`);
  });

  if (!aperto) {
    return (
      <Button className="shrink-0 text-xs" onClick={() => setAperto(true)}>
        {t.t("proxy.installaNpm")}
      </Button>
    );
  }

  return (
    <div className="w-full space-y-2 rounded-lg border p-3">
      <div className="grid grid-cols-2 gap-2">
        <Field label={t.t("proxy.portaHttp")}>
          <Input type="number" min={1} max={65535} value={http} onChange={(e) => setHttp(e.target.value)} />
        </Field>
        <Field label={t.t("proxy.portaHttps")}>
          <Input type="number" min={1} max={65535} value={https} onChange={(e) => setHttps(e.target.value)} />
        </Field>
      </div>
      <p className="tenue">{t.t("proxy.porteAiuto")}</p>
      <ErrorNotice error={installazione.error} />
      <div className="flex gap-2">
        <Button
          className="text-xs"
          loading={installazione.running}
          onClick={() => void installazione.run()}
        >
          {t.t("proxy.installa")}
        </Button>
        <Button className="text-xs" onClick={() => setAperto(false)}>
          {t.t("comune.annulla")}
        </Button>
      </div>
    </div>
  );
}

function PannelloInstradamenti({
  stackId,
  dati: data,
  modificabile,
  onInstradamentiAggiornati,
}: {
  stackId: number;
  dati: DatiInstradamenti;
  modificabile: boolean;
  /** Ricarica il verdetto: lo mostrano anche il percorso e la striscia in alto. */
  onInstradamentiAggiornati: () => void;
}) {
  const t = useT();
  /** Servizio che si sta instradando: il modulo compare sulla sua riga. */
  const [inCorso, setInCorso] = useState<string | null>(null);
  const [domain, setDomain] = useState("");
  const [containerPort, setContainerPort] = useState("");

  const salvataggio = useAction(async () => {
    await api.put(`/stacks/${stackId}/instradamenti`, {
      service: inCorso,
      domain,
      containerPort: Number(containerPort),
    });
    setInCorso(null);
    setDomain("");
    onInstradamentiAggiornati();
  });

  const rimozione = useAction(async (servizio: string) => {
    await api.delete(`/stacks/${stackId}/instradamenti/${encodeURIComponent(servizio)}`);
    onInstradamentiAggiornati();
  });

  /** Apre il modulo con la porta già dedotta dal compose, se il compose la dichiara. */
  const iniziaInstradamento = (servizio: string, porta: number | null, dominio: string) => {
    setInCorso(servizio);
    // Un valore plausibile e sbagliato che sembra confermato instrada il dominio
    // su una porta chiusa, e l'errore torna come 502 dal proxy, dove nessuno lo
    // collega a questa casella.
    setContainerPort(porta === null ? "" : String(porta));
    setDomain(dominio);
  };

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold">{t.t("rilascioUi.instradamenti.titolo")}</h2>
        <p className="mt-0.5 text-sm tenue">{t.t("rilascioUi.instradamenti.intro")}</p>
      </div>

      <ErrorNotice error={salvataggio.error} />

      {(() => {
        const fissi = data.servizi.filter((s) => s.fisso);
        return (
          <>
            {fissi.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs tenue">{t.t("rilascioUi.fissi")}</p>
                <Card className="divide-y overflow-hidden">
                  {fissi.map((servizio) => (
                    <div key={servizio.name} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                      <code className="font-mono font-medium">{servizio.name}</code>
                      <Badge tone="neutral">{t.t("rilascioUi.fisso")}</Badge>
                    </div>
                  ))}
                </Card>
              </div>
            )}
            <ElencoServizi
              servizi={serviziInstradabili(data.servizi, data.routes)}
              modificabile={modificabile}
              inCorso={inCorso}
              domain={domain}
              containerPort={containerPort}
              onDomain={setDomain}
              onContainerPort={setContainerPort}
              onInizia={iniziaInstradamento}
              onAnnulla={() => setInCorso(null)}
              onSalva={() => void salvataggio.run()}
              onRimuovi={(servizio) => void rimozione.run(servizio)}
              salvando={salvataggio.running}
            />
          </>
        );
      })()}

      <ErrorNotice error={rimozione.error} />
    </section>
  );
}

/**
 * I servizi dello stack, con il proprio instradamento o la possibilità di darne uno.
 *
 * Sostituisce un modulo con tre campi liberi: con dodici servizi, ricordare i
 * nomi e andare a cercare le porte interne nel compose era il lavoro vero, e non
 * c'era modo di sapere quali servizi avesse senso instradare.
 */
function ElencoServizi({
  servizi,
  modificabile,
  inCorso,
  domain,
  containerPort,
  onDomain,
  onContainerPort,
  onInizia,
  onAnnulla,
  onSalva,
  onRimuovi,
  salvando,
}: {
  servizi: readonly ServizioInstradabile[];
  modificabile: boolean;
  inCorso: string | null;
  domain: string;
  containerPort: string;
  onDomain: (valore: string) => void;
  onContainerPort: (valore: string) => void;
  onInizia: (servizio: string, porta: number | null, dominio: string) => void;
  onAnnulla: () => void;
  onSalva: () => void;
  onRimuovi: (servizio: string) => void;
  salvando: boolean;
}) {
  const t = useT();
  if (servizi.length === 0) return null;

  return (
    <div className="space-y-3">
      <Card className="divide-y overflow-hidden">
        {servizi.map((servizio) => (
          <div key={servizio.servizio} className="px-4 py-2.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <code className="font-mono font-medium">{servizio.servizio}</code>

              {servizio.instradato ? (
                <>
                  <span className="tenue">→</span>
                  <span className="min-w-0 truncate">{servizio.instradato.domain}</span>
                  <Badge tone="success">{t.t("rilascioUi.instradato")}</Badge>
                </>
              ) : (
                <span className="text-xs tenue">
                  {servizio.portaProposta === null
                    ? t.t("rilascioUi.portaDaIndicare")
                    : t.t("rilascioUi.portaInterna", { porta: servizio.portaProposta })}
                  {servizio.piuPorte && ` ${t.t("rilascioUi.piuPorte")}`}
                </span>
              )}

              {!servizio.nelCompose && <Badge tone="danger">{t.t("rilascioUi.nonNelCompose")}</Badge>}

              {modificabile && inCorso !== servizio.servizio && (
                <div className="ml-auto flex gap-2">
                  <Button
                    className="text-xs"
                    onClick={() =>
                      onInizia(
                        servizio.servizio,
                        servizio.instradato?.containerPort ?? servizio.portaProposta,
                        servizio.instradato?.domain ?? "",
                      )
                    }
                  >
                    {t.t(servizio.instradato ? "rilascioUi.modifica" : "rilascioUi.instrada")}
                  </Button>

                  {servizio.instradato && (
                    <Button
                      variant="ghost"
                      className="text-xs text-red-600"
                      onClick={() => onRimuovi(servizio.servizio)}
                    >
                      {t.t("rilascioUi.rimuovi")}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {inCorso === servizio.servizio && (
              <div className="mt-3 space-y-3 border-t pt-3">
                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <Field label={t.t("rilascioUi.dominio")}>
                    <Input
                      value={domain}
                      onChange={(e) => onDomain(e.target.value)}
                      placeholder="app.esempio.it" // i18n-ignora
                      autoFocus
                    />
                  </Field>
                  <Field label={t.t("rilascioUi.portaInternaCampo")}>
                    <Input
                      value={containerPort}
                      onChange={(e) => onContainerPort(e.target.value)}
                      inputMode="numeric"
                      className="w-28"
                    />
                  </Field>
                </div>

                {/* Il proxy riconosce il dominio dall'intestazione Host e non
                    lo risolve: che esista in un DNS riguarda solo chi apre il
                    browser. È la confusione più comune quando si prova in
                    locale, dove sembra che serva un dominio vero. */}
                <p className="text-xs tenue">{t.t("rilascioUi.dominioAiuto")}</p>

                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    loading={salvando}
                    disabled={!domain || !containerPort}
                    onClick={onSalva}
                  >
                    {t.t("comune.salva")}
                  </Button>
                  <Button variant="ghost" className="text-xs" onClick={onAnnulla}>
                    {t.t("comune.annulla")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}
